import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { SearchService, SearchResult } from 'src/app/services/search/search.service';
import {
  SearchScope,
  getSearchAvatarLabel,
  getSearchBadgeLabel,
  getSearchEntityType,
  getSearchImageUrl,
  getSearchPrimaryLabel,
  getSearchSecondaryLabel,
} from 'src/app/services/search/search.helpers';

@Component({
  selector: 'app-search',
  templateUrl: './search.component.html',
  styleUrls: ['./search.component.css']
})
export class SearchComponent implements OnInit, OnDestroy {
  keyword: string = '';
  results: SearchResult[] = [];
  loading: boolean = false;
  error: string = '';
  currentPage: number = 0;
  pageSize: number = 10;
  totalElements: number = 0;
  searchScope: SearchScope = 'public';

  private destroy$ = new Subject<void>();

  constructor(
    private searchService: SearchService,
    private route: ActivatedRoute,
    private router: Router,
  ) {}

  ngOnInit(): void {
    // Get search keyword from query params
    this.route.queryParams
      .pipe(takeUntil(this.destroy$))
      .subscribe((params) => {
        this.keyword = params['q'] || '';
        this.currentPage = parseInt(params['page'] || '0', 10);
        this.searchScope = params['scope'] === 'admin' ? 'admin' : this.getRouteScope();
        
        if (this.keyword.trim().length >= 2) {
          this.performSearch();
        } else {
          this.results = [];
          this.totalElements = 0;
          this.error = '';
        }
      });
  }

  performSearch(): void {
    if (this.keyword.trim().length < 2) {
      this.error = 'Please enter at least 2 characters';
      return;
    }

    this.loading = true;
    this.error = '';

    const searchFn = this.searchService.searchPublic(
      this.keyword,
      this.currentPage,
      this.pageSize
    );

    searchFn.pipe(takeUntil(this.destroy$)).subscribe(
      (response) => {
        const normalized = this.searchService.normalizeResponse(response);
        this.results = normalized.content || [];
        this.totalElements = normalized.totalElements || 0;
        this.loading = false;

        if (this.results.length === 0) {
          this.error = `No results found for "${this.keyword}"`;
        }
      },
      (error) => {
        console.error('Search error:', error);
        this.error = 'An error occurred during search. Please try again.';
        this.loading = false;
      }
    );
  }

  selectResult(result: SearchResult): void {
    const route = this.resolveSearchRoute(result);
    if (route) {
      this.router.navigate(route);
    }
  }

  getResultIcon(type?: string): string {
    switch (type?.toUpperCase()) {
      case 'JOB':
      case 'JOB_POST':
        return 'bi bi-briefcase';
      case 'COMPANY':
        return 'bi bi-building';
      case 'USER':
        return 'bi bi-person';
      default:
        return 'bi bi-search';
    }
  }

  getResultTitle(result: SearchResult): string {
    return getSearchPrimaryLabel(result);
  }

  getResultSubtitle(result: SearchResult): string {
    return getSearchSecondaryLabel(result) || getSearchBadgeLabel(result);
  }

  getResultAvatarLabel(result: SearchResult): string {
    return getSearchAvatarLabel(result);
  }

  getResultImage(result: SearchResult): string {
    return getSearchImageUrl(result);
  }

  getResultDescription(result: SearchResult): string {
    return result.description || result.jobDescription || '';
  }

  hasLongDescription(result: SearchResult): boolean {
    return this.getResultDescription(result).length > 100;
  }

  private resolveSearchRoute(result: SearchResult): any[] | null {
    const entityType = getSearchEntityType(result);
    const explicitRoute = result.route || (this.searchScope === 'admin' ? result.adminRoute : result.publicRoute);

    if (explicitRoute) {
      const normalizedRoute = explicitRoute.startsWith('/') ? explicitRoute : `/${explicitRoute}`;
      return [normalizedRoute];
    }

    if (this.searchScope === 'admin') {
      if (entityType === 'JOB') {
        return ['/company-admin/edit-job', result.id];
      }

      if (entityType === 'COMPANY') {
        return ['/company-admin/company-profile'];
      }

      if (entityType === 'USER') {
        return ['/company-admin/application', result.id];
      }
    } else {
      if (entityType === 'JOB') {
        return ['/job-description', result.id];
      }

      if (entityType === 'COMPANY') {
        return ['/company-profile', result.companyId || result.id];
      }

      if (entityType === 'USER') {
        return ['/profile'];
      }
    }

    return null;
  }

  onPageChange(page: number): void {
    this.currentPage = page;
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { q: this.keyword, page: page, scope: this.searchScope },
      queryParamsHandling: 'merge'
    });
  }

  getTotalPages(): number {
    return Math.ceil(this.totalElements / this.pageSize);
  }

  private getRouteScope(): SearchScope {
    return this.router.url.includes('/company-admin') ? 'admin' : 'public';
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
